import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { socialService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { useNavigate } from 'react-router-dom'
import Lottie from './Lottie'
import SharedCartPreview from './SharedCartPreview'
import SharedCartAttachment from './SharedCartAttachment'
import RepostModal from './RepostModal'
import './PostCard.css'

function PostCard({ post, onUpdate, onUserClick, isNested = false }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showComments, setShowComments] = useState(false)
  const [commentContent, setCommentContent] = useState('')
  const [isAnimating, setIsAnimating] = useState(false)
  const [commentAnimation, setCommentAnimation] = useState(null)
  const commentLottieRef = useRef(null)
  const [showMenu, setShowMenu] = useState(false)
  const [deleteAnimation, setDeleteAnimation] = useState(null)
  const [editAnimation, setEditAnimation] = useState(null)
  const [pingAnimation, setPingAnimation] = useState(null)
  const [shareAnimation, setShareAnimation] = useState(null)
  const [successAnimation, setSuccessAnimation] = useState(null)
  const [refuseAnimation, setRefuseAnimation] = useState(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [successMessage, setSuccessMessage] = useState('Post supprimé avec succès')
  const [isDeleteAction, setIsDeleteAction] = useState(false)
  const deleteLottieRef = useRef(null)
  const editLottieRef = useRef(null)
  const pingLottieRef = useRef(null)
  const shareLottieRef = useRef(null)
  const successLottieRef = useRef(null)
  const refuseLottieRef = useRef(null)
  const menuRef = useRef(null)
  const { addNotification } = useNotifications()

  // Charger les animations
  useEffect(() => {
    fetch('/Comment.json')
      .then(res => res.json())
      .then(data => setCommentAnimation(data))
      .catch(err => console.error('Erreur chargement animation Comment:', err))
    
    fetch('/Delete_animation.json')
      .then(res => res.json())
      .then(data => setDeleteAnimation(data))
      .catch(err => console.error('Erreur chargement animation Delete:', err))
    
    fetch('/Edit_animation.json')
      .then(res => res.json())
      .then(data => setEditAnimation(data))
      .catch(err => console.error('Erreur chargement animation Edit:', err))
    
    fetch('/Ping.json')
      .then(res => res.json())
      .then(data => setPingAnimation(data))
      .catch(err => console.error('Erreur chargement animation Ping:', err))
    
    fetch('/Share.json')
      .then(res => res.json())
      .then(data => setShareAnimation(data))
      .catch(err => console.error('Erreur chargement animation Share:', err))
    
    fetch('/Succes.json')
      .then(res => res.json())
      .then(data => setSuccessAnimation(data))
      .catch(err => console.error('Erreur chargement animation Succes:', err))
    
    fetch('/refuse.json')
      .then(res => res.json())
      .then(data => setRefuseAnimation(data))
      .catch(err => console.error('Erreur chargement animation refuse:', err))
  }, [])

  // Fermer le menu quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  // L'animation sera jouée automatiquement avec autoplay={true}

  const isLiked = post.likes?.some(like => like.user_id === user?.id) || false
  const isReposted = post.is_reposted || post.reposts?.some(repost => repost.author_id === user?.id) || false
  const likesCount = post.likes_count || 0
  const repostsCount = post.reposts_count || 0
  const commentsCount = post.comments_count || 0

  // Récupérer les commentaires (pour le post ou le repost)
  const { data: commentsData } = useQuery({
    queryKey: ['post-comments', post.id],
    queryFn: () => socialService.getComments(post.id),
    enabled: showComments
  })

  const comments = commentsData?.comments || []

  // Mutation pour liker/unliker
  const likeMutation = useMutation({
    mutationFn: () => {
      // Déterminer si le post est liké (pour le post normal ou le repost)
      const currentIsLiked = (post.repost_of_id || post.original_post) 
        ? (post.likes?.some(like => like.user_id === user?.id) || false)
        : isLiked
      return currentIsLiked ? socialService.unlikePost(post.id) : socialService.likePost(post.id)
    },
    onSuccess: () => {
      // Mettre à jour optimistiquement les données du feed au lieu d'invalider
      queryClient.setQueryData(['social-feed'], (oldData) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          posts: oldData.posts.map(p => {
            if (p.id === post.id) {
              const currentIsLiked = (p.repost_of_id || p.original_post)
                ? (p.likes?.some(like => like.user_id === user?.id) || false)
                : (p.likes?.some(like => like.user_id === user?.id) || false)
              const newLikes = currentIsLiked 
                ? (p.likes || []).filter(like => like.user_id !== user?.id)
                : [...(p.likes || []), { user_id: user?.id }]
              return {
                ...p,
                likes: newLikes,
                likes_count: currentIsLiked ? (p.likes_count || 0) - 1 : (p.likes_count || 0) + 1
              }
            }
            return p
          })
        }
      })
      // Invalider seulement si onUpdate est fourni (pour les autres vues)
      if (onUpdate) {
        onUpdate()
      }
    },
    onError: (error) => {
      // En cas d'erreur, invalider pour récupérer les vraies données
      queryClient.invalidateQueries(['social-feed'])
      if (onUpdate) onUpdate()
    }
  })

  // Mutation pour créer un commentaire
  const createCommentMutation = useMutation({
    mutationFn: (content) => socialService.createComment(post.id, content),
    onSuccess: () => {
      setCommentContent('')
      queryClient.invalidateQueries(['post-comments', post.id])
      queryClient.invalidateQueries(['social-feed'])
      if (onUpdate) onUpdate()
    }
  })

  // Mutation pour supprimer un post
  const deleteMutation = useMutation({
    mutationFn: () => socialService.deletePost(post.id),
    onSuccess: () => {
      // Afficher le message de suppression avec animation refuse (comme pour demande d'ami déjà envoyée)
      console.log('[PostCard] Suppression réussie, affichage du message de suppression')
      console.log('[PostCard] refuseAnimation chargée:', !!refuseAnimation)
      setSuccessMessage(post.repost_of_id ? 'Repost supprimé' : 'Post supprimé')
      setIsDeleteAction(true)
      setShowSuccessMessage(true)
      // Masquer après 3 secondes et mettre à jour le feed
      setTimeout(() => {
        setShowSuccessMessage(false)
        // Mettre à jour le feed après l'animation
        queryClient.invalidateQueries(['social-feed'])
        if (onUpdate) onUpdate()
      }, 3000)
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.detail || 'Erreur lors de la suppression'
      addNotification({
        type: 'error',
        title: 'Suppression impossible',
        message: errorMessage,
      })
      console.error('[PostCard] Erreur lors de la suppression:', error)
    }
  })

  // Mutation pour unrepost (annuler un repost)
  const repostMutation = useMutation({
    mutationFn: () => socialService.unrepostPost(post.id),
    onSuccess: () => {
      // Mettre à jour optimistiquement les données du feed
      queryClient.setQueryData(['social-feed'], (oldData) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          posts: oldData.posts.map(p => {
            if (p.id === post.id) {
              const newReposts = isReposted 
                ? (p.reposts || []).filter(repost => repost.user_id !== user?.id)
                : [...(p.reposts || []), { user_id: user?.id }]
              return {
                ...p,
                reposts: newReposts,
                reposts_count: isReposted ? (p.reposts_count || 0) - 1 : (p.reposts_count || 0) + 1
              }
            }
            return p
          })
        }
      })
      if (onUpdate) {
        onUpdate()
      }
    },
    onError: (error) => {
      console.error('Erreur lors du repost:', error)
      // Si l'erreur indique que le repost existe déjà, mettre à jour l'état local
      const errorMessage = error?.response?.data?.detail || error?.message || ''
      if (errorMessage.includes('déjà reposté') || errorMessage.includes('déjà')) {
        // Le repost existe déjà, mettre à jour l'état local
        queryClient.setQueryData(['social-feed'], (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            posts: oldData.posts.map(p => {
              if (p.id === post.id) {
                // Ajouter le repost à la liste si ce n'est pas déjà fait
                const hasRepost = (p.reposts || []).some(repost => repost.user_id === user?.id)
                if (!hasRepost) {
                  return {
                    ...p,
                    reposts: [...(p.reposts || []), { user_id: user?.id }],
                    reposts_count: (p.reposts_count || 0) + 1
                  }
                }
              }
              return p
            })
          }
        })
        if (onUpdate) onUpdate()
      } else {
        // Pour les autres erreurs, invalider les queries
        queryClient.invalidateQueries(['social-feed'])
        if (onUpdate) onUpdate()
      }
    }
  })

  const handleLike = () => {
    if (likeMutation.isLoading) return
    // Déclencher l'animation à chaque clic
    setIsAnimating(true)
    setTimeout(() => setIsAnimating(false), 1000)
    likeMutation.mutate()
  }

  const handleComment = (e) => {
    e.preventDefault()
    if (!commentContent.trim()) return
    createCommentMutation.mutate(commentContent.trim())
  }

  const handleDelete = () => {
    setShowMenu(false)
    if (deleteLottieRef.current) {
      deleteLottieRef.current.goToAndPlay(0)
    }
    deleteMutation.mutate()
  }

  const handleEdit = () => {
    setShowMenu(false)
    if (editLottieRef.current) {
      editLottieRef.current.goToAndPlay(0)
    }
    // TODO: Implémenter l'édition
    console.log('Édition du post:', post.id)
  }

  const handlePin = () => {
    setShowMenu(false)
    if (pingLottieRef.current) {
      pingLottieRef.current.goToAndPlay(0)
    }
    // TODO: Implémenter l'épinglage
    console.log('Épinglage du post:', post.id)
  }

  const [showRepostModal, setShowRepostModal] = useState(false)

  const handleRepost = () => {
    if (isReposted) {
      // Si déjà reposté, annuler le repost directement
      if (repostMutation.isLoading) return
      if (shareLottieRef.current) {
        shareLottieRef.current.goToAndPlay(0)
      }
      repostMutation.mutate()
    } else {
      // Sinon, ouvrir le modal pour ajouter un commentaire
      setShowRepostModal(true)
    }
  }

  const handleRepostSuccess = () => {
    // Afficher le message de succès avec animation pour le repost
    console.log('[PostCard] Repost réussi, affichage du message de succès')
    setSuccessMessage('Post reposté avec succès')
    setIsDeleteAction(false)
    setShowSuccessMessage(true)
    // Masquer après 3 secondes
    setTimeout(() => {
      setShowSuccessMessage(false)
    }, 3000)
    // Mettre à jour optimistiquement les données du feed
    queryClient.setQueryData(['social-feed'], (oldData) => {
      if (!oldData) return oldData
      return {
        ...oldData,
        posts: oldData.posts.map(p => {
          if (p.id === post.id) {
            return {
              ...p,
              is_reposted: true,
              reposts_count: (p.reposts_count || 0) + 1
            }
          }
          return p
        })
      }
    })
    if (onUpdate) {
      onUpdate()
    }
  }

  const [showCartPreview, setShowCartPreview] = useState(false)

  const handleViewCart = () => {
    if (post.shared_cart) {
      setShowCartPreview(true)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'À l\'instant'
    if (minutes < 60) return `Il y a ${minutes} min`
    if (hours < 24) return `Il y a ${hours}h`
    if (days < 7) return `Il y a ${days}j`
    return date.toLocaleDateString('fr-FR')
  }

  // Si c'est un repost, afficher le post original
  const isRepost = post.repost_of_id || post.original_post
  const originalPost = post.original_post

  // Si c'est un repost et qu'on n'est pas déjà dans un post imbriqué, afficher le header de repost
  if (isRepost && originalPost && !isNested) {
    // Utiliser les données du repost pour les interactions (like, comment, repost du repost lui-même)
    const repostIsLiked = post.likes?.some(like => like.user_id === user?.id) || false
    const repostIsReposted = post.is_reposted || post.reposts?.some(repost => repost.author_id === user?.id) || false
    const repostLikesCount = post.likes_count || 0
    const repostRepostsCount = post.reposts_count || 0
    const repostCommentsCount = post.comments_count || 0
    // Utiliser les commentaires déjà récupérés (pour le repost, post.id est l'ID du repost, donc comments contient les commentaires du repost)
    const repostComments = comments

    return (
      <div className="post-card repost-card">
        <div className="repost-header">
          <div className="repost-author-info" onClick={() => onUserClick && onUserClick(post.author?.id)}>
            <div 
              className="repost-author-avatar"
              style={{
                backgroundImage: post.author?.avatar_url ? `url(${post.author.avatar_url})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              {!post.author?.avatar_url && (
                (post.author?.display_name?.charAt(0) || 
                 post.author?.username?.charAt(0) || 
                 post.author?.email?.charAt(0) || 
                 'U').toUpperCase()
              )}
            </div>
            <div className="repost-author-details">
              <span className="repost-author-name">
                {post.author?.display_name || 
                 (post.author?.username ? `@${post.author.username}` : null) || 
                 post.author?.email || 
                 'Utilisateur'}
              </span>
              {post.author?.username && post.author?.display_name && (
                <span className="repost-author-username">@{post.author.username}</span>
              )}
            </div>
          </div>
          <div className="repost-indicator">
            <img src="/Report_icon.png" alt="Repost" className="repost-icon" />
            <span className="repost-text">a reposté</span>
            <span className="repost-date">{formatDate(post.created_at)}</span>
          </div>
          {post.author?.id === user?.id && (
            <div className="post-menu-container" ref={menuRef} style={{ display: 'flex', flexShrink: 0 }}>
              <button 
                className="menu-toggle-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowMenu(!showMenu)
                }}
                title="Options"
                aria-label="Options du repost"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="5" r="2" fill="currentColor"></circle>
                  <circle cx="12" cy="12" r="2" fill="currentColor"></circle>
                  <circle cx="12" cy="19" r="2" fill="currentColor"></circle>
                </svg>
              </button>
              {showMenu && (
                <div className="post-menu-dropdown">
                  <button className="menu-item menu-item-delete" onClick={handleDelete}>
                    {deleteAnimation ? (
                      <Lottie
                        lottieRef={deleteLottieRef}
                        animationData={deleteAnimation}
                        loop={false}
                        autoplay={false}
                        style={{ width: 20, height: 20 }}
                      />
                    ) : (
                      <span className="menu-icon">🗑️</span>
                    )}
                    <span>Supprimer</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {post.content && post.content.trim() && post.content.trim() !== " " && (
          <div className="repost-comment">
            <p>{post.content}</p>
          </div>
        )}
        <div className="original-post-container">
          <PostCard post={originalPost} onUpdate={onUpdate} onUserClick={onUserClick} isNested={true} />
        </div>
        
        {/* Actions pour le repost lui-même */}
        <div className="repost-actions">
          <div className="like-btn-wrapper">
            <div 
              className={`heart-container ${repostIsLiked || isAnimating ? 'checked' : ''}`}
              onClick={handleLike}
              style={{ cursor: likeMutation.isLoading ? 'not-allowed' : 'pointer', pointerEvents: likeMutation.isLoading ? 'none' : 'auto' }}
            >
              <div className="svg-container">
                <svg viewBox="0 0 24 24" className="svg-outline" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.5,1.917a6.4,6.4,0,0,0-5.5,3.3,6.4,6.4,0,0,0-5.5-3.3A6.8,6.8,0,0,0,0,8.967c0,4.547,4.786,9.513,8.8,12.88a4.974,4.974,0,0,0,6.4,0C19.214,18.48,24,13.514,24,8.967A6.8,6.8,0,0,0,17.5,1.917Zm-3.585,18.4a2.973,2.973,0,0,1-3.83,0C4.947,16.006,2,11.87,2,8.967a4.8,4.8,0,0,1,4.5-5.05A4.8,4.8,0,0,1,11,8.967a1,1,0,0,0,2,0,4.8,4.8,0,0,1,4.5-5.05A4.8,4.8,0,0,1,22,8.967C22,11.87,19.053,16.006,13.915,20.313Z">
                  </path>
                </svg>
                <svg viewBox="0 0 24 24" className="svg-filled" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.5,1.917a6.4,6.4,0,0,0-5.5,3.3,6.4,6.4,0,0,0-5.5-3.3A6.8,6.8,0,0,0,0,8.967c0,4.547,4.786,9.513,8.8,12.88a4.974,4.974,0,0,0,6.4,0C19.214,18.48,24,13.514,24,8.967A6.8,6.8,0,0,0,17.5,1.917Z">
                  </path>
                </svg>
                <svg className="svg-celebrate" width="100" height="100" xmlns="http://www.w3.org/2000/svg">
                  <polygon points="10,10 20,20" />
                  <polygon points="10,50 20,50" />
                  <polygon points="20,80 30,70" />
                  <polygon points="90,10 80,20" />
                  <polygon points="90,50 80,50" />
                  <polygon points="80,80 70,70" />
                </svg>
              </div>
            </div>
            <span style={{ color: repostIsLiked ? '#ff6b6b' : 'var(--text-secondary)' }}>{repostLikesCount}</span>
          </div>

          <button
            className="action-btn comment-btn"
            onClick={() => {
              setShowComments(!showComments)
              if (commentLottieRef.current) {
                commentLottieRef.current.goToAndPlay(0)
              }
            }}
          >
            {commentAnimation ? (
              <Lottie
                lottieRef={commentLottieRef}
                animationData={commentAnimation}
                loop={false}
                autoplay={false}
                style={{ width: 24, height: 24 }}
              />
            ) : (
              <span className="action-icon">💬</span>
            )}
            <span>{repostCommentsCount}</span>
          </button>

          <button
            className={`action-btn repost-btn ${repostIsReposted ? 'reposted' : ''}`}
            onClick={handleRepost}
            disabled={repostMutation.isLoading}
            style={{ cursor: repostMutation.isLoading ? 'not-allowed' : 'pointer' }}
          >
            {shareAnimation ? (
              <Lottie
                lottieRef={shareLottieRef}
                animationData={shareAnimation}
                loop={false}
                autoplay={false}
                style={{ width: 24, height: 24 }}
              />
            ) : (
              <img src="/Report_icon.png" alt="Repost" className="action-icon repost-action-icon" />
            )}
            <span style={{ color: repostIsReposted ? '#4a9eff' : 'var(--text-secondary)' }}>{repostRepostsCount}</span>
          </button>
        </div>

        {showComments && (
          <div className="comments-section">
            <div className="comments-list">
              {repostComments.length === 0 ? (
                <div className="no-comments">Aucun commentaire pour le moment</div>
              ) : (
                repostComments.map(comment => (
                  <div key={comment.id} className="comment-item">
                    <div 
                      className="comment-author"
                      style={{
                        backgroundImage: comment.author?.avatar_url ? `url(${comment.author.avatar_url})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      {!comment.author?.avatar_url && (
                        (comment.author?.display_name?.charAt(0) || 
                         comment.author?.username?.charAt(0) || 
                         comment.author?.email?.charAt(0) || 
                         'U').toUpperCase()
                      )}
                    </div>
                    <div className="comment-content">
                      <div className="comment-header">
                        <span className="comment-author-name">
                          {comment.author?.display_name || 
                           (comment.author?.username ? `@${comment.author.username}` : null) || 
                           comment.author?.email || 
                           'Utilisateur'}
                        </span>
                        <span className="comment-date">{formatDate(comment.created_at)}</span>
                      </div>
                      <p>{comment.content}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleComment} className="comment-form">
              <input
                type="text"
                className="comment-input"
                placeholder="Écrire un commentaire..."
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
              />
              <button
                type="submit"
                className="comment-submit"
                disabled={!commentContent.trim() || createCommentMutation.isLoading}
              >
                Publier
              </button>
            </form>
          </div>
        )}

        <RepostModal
          post={post}
          isOpen={showRepostModal}
          onClose={() => setShowRepostModal(false)}
          onSuccess={handleRepostSuccess}
        />
      </div>
    )
  }

  return (
    <div className="post-card">
      <div className="post-header">
        <div className="post-author" onClick={() => onUserClick && onUserClick(post.author.id)}>
          <div 
            className="author-avatar"
            style={{
              backgroundImage: post.author?.avatar_url ? `url(${post.author.avatar_url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {!post.author?.avatar_url && (
              (post.author?.display_name?.charAt(0) || 
               post.author?.username?.charAt(0) || 
               post.author?.email?.charAt(0) || 
               'U').toUpperCase()
            )}
          </div>
          <div className="author-info">
            <div className="author-name">
              {post.author?.display_name || 
               (post.author?.username ? `@${post.author.username}` : null) || 
               post.author?.email || 
               'Utilisateur'}
            </div>
            {post.author?.username && post.author?.display_name && (
              <div className="author-username">@{post.author.username}</div>
            )}
            <div className="post-date">{formatDate(post.created_at)}</div>
          </div>
        </div>
        {post.author?.id === user?.id && (
          <div className="post-menu-container" ref={menuRef}>
            <button 
              className="menu-toggle-btn"
              onClick={(e) => {
                e.stopPropagation()
                setShowMenu(!showMenu)
              }}
              title="Options"
              aria-label="Options du post"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="5" r="2" fill="currentColor"></circle>
                <circle cx="12" cy="12" r="2" fill="currentColor"></circle>
                <circle cx="12" cy="19" r="2" fill="currentColor"></circle>
              </svg>
            </button>
            {showMenu && (
              <div className="post-menu-dropdown">
                <button className="menu-item menu-item-delete" onClick={handleDelete}>
                  {deleteAnimation ? (
                    <Lottie
                      lottieRef={deleteLottieRef}
                      animationData={deleteAnimation}
                      loop={false}
                      autoplay={false}
                      style={{ width: 20, height: 20 }}
                    />
                  ) : (
                    <span className="menu-icon">🗑️</span>
                  )}
                  <span>Supprimer</span>
                </button>
                <button className="menu-item menu-item-edit" onClick={handleEdit}>
                  {editAnimation ? (
                    <Lottie
                      lottieRef={editLottieRef}
                      animationData={editAnimation}
                      loop={false}
                      autoplay={false}
                      style={{ width: 20, height: 20 }}
                    />
                  ) : (
                    <span className="menu-icon">✏️</span>
                  )}
                  <span>Éditer</span>
                </button>
                <button className="menu-item menu-item-pin" onClick={handlePin}>
                  {pingAnimation ? (
                    <Lottie
                      lottieRef={pingLottieRef}
                      animationData={pingAnimation}
                      loop={false}
                      autoplay={false}
                      style={{ width: 20, height: 20 }}
                    />
                  ) : (
                    <span className="menu-icon">📌</span>
                  )}
                  <span>Épingler</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="post-content">
        <p>{post.content}</p>
        
        {post.shared_cart && (
          <SharedCartAttachment
            cart={post.shared_cart}
            sharedCartId={post.shared_cart.id}
            variant="feed"
            playKey={`feed:${post.id}`}
            onOpen={handleViewCart}
          />
        )}
      </div>

      <div className="post-actions">
        <div className="like-btn-wrapper">
          <div 
            className={`heart-container ${isLiked || isAnimating ? 'checked' : ''}`}
            onClick={handleLike}
            style={{ cursor: likeMutation.isLoading ? 'not-allowed' : 'pointer', pointerEvents: likeMutation.isLoading ? 'none' : 'auto' }}
          >
            <div className="svg-container">
              <svg viewBox="0 0 24 24" className="svg-outline" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.5,1.917a6.4,6.4,0,0,0-5.5,3.3,6.4,6.4,0,0,0-5.5-3.3A6.8,6.8,0,0,0,0,8.967c0,4.547,4.786,9.513,8.8,12.88a4.974,4.974,0,0,0,6.4,0C19.214,18.48,24,13.514,24,8.967A6.8,6.8,0,0,0,17.5,1.917Zm-3.585,18.4a2.973,2.973,0,0,1-3.83,0C4.947,16.006,2,11.87,2,8.967a4.8,4.8,0,0,1,4.5-5.05A4.8,4.8,0,0,1,11,8.967a1,1,0,0,0,2,0,4.8,4.8,0,0,1,4.5-5.05A4.8,4.8,0,0,1,22,8.967C22,11.87,19.053,16.006,13.915,20.313Z">
                </path>
              </svg>
              <svg viewBox="0 0 24 24" className="svg-filled" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.5,1.917a6.4,6.4,0,0,0-5.5,3.3,6.4,6.4,0,0,0-5.5-3.3A6.8,6.8,0,0,0,0,8.967c0,4.547,4.786,9.513,8.8,12.88a4.974,4.974,0,0,0,6.4,0C19.214,18.48,24,13.514,24,8.967A6.8,6.8,0,0,0,17.5,1.917Z">
                </path>
              </svg>
              <svg className="svg-celebrate" width="100" height="100" xmlns="http://www.w3.org/2000/svg">
                <polygon points="10,10 20,20" />
                <polygon points="10,50 20,50" />
                <polygon points="20,80 30,70" />
                <polygon points="90,10 80,20" />
                <polygon points="90,50 80,50" />
                <polygon points="80,80 70,70" />
              </svg>
            </div>
          </div>
          <span style={{ color: isLiked ? '#ff6b6b' : 'var(--text-secondary)' }}>{likesCount}</span>
        </div>

        <button
          className="action-btn comment-btn"
          onClick={() => {
            setShowComments(!showComments)
            // Jouer l'animation Comment au clic
            if (commentLottieRef.current) {
              commentLottieRef.current.goToAndPlay(0)
            }
          }}
        >
          {commentAnimation ? (
            <Lottie
              lottieRef={commentLottieRef}
              animationData={commentAnimation}
              loop={false}
              autoplay={false}
              style={{ width: 24, height: 24 }}
            />
          ) : (
            <span className="action-icon">💬</span>
          )}
          <span>{commentsCount}</span>
        </button>

        <button
          className={`action-btn repost-btn ${isReposted ? 'reposted' : ''}`}
          onClick={handleRepost}
          disabled={repostMutation.isLoading}
          style={{ cursor: repostMutation.isLoading ? 'not-allowed' : 'pointer' }}
        >
          {shareAnimation ? (
            <Lottie
              lottieRef={shareLottieRef}
              animationData={shareAnimation}
              loop={false}
              autoplay={false}
              style={{ width: 24, height: 24 }}
            />
          ) : (
            <img src="/Report_icon.png" alt="Repost" className="action-icon repost-action-icon" />
          )}
          <span style={{ color: isReposted ? '#4a9eff' : 'var(--text-secondary)' }}>{repostsCount}</span>
        </button>
      </div>

      {showComments && (
        <div className="comments-section">
          <div className="comments-list">
            {comments.length === 0 ? (
              <div className="no-comments">Aucun commentaire pour le moment</div>
            ) : (
              comments.map(comment => (
                <div key={comment.id} className="comment-item">
                  <div 
                    className="comment-author"
                    style={{
                      backgroundImage: comment.author?.avatar_url ? `url(${comment.author.avatar_url})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {!comment.author?.avatar_url && (
                      (comment.author?.display_name?.charAt(0) || 
                       comment.author?.username?.charAt(0) || 
                       comment.author?.email?.charAt(0) || 
                       'U').toUpperCase()
                    )}
                  </div>
                  <div className="comment-content">
                    <div className="comment-header">
                      <span className="comment-author-name">
                        {comment.author?.display_name || 
                         (comment.author?.username ? `@${comment.author.username}` : null) || 
                         comment.author?.email || 
                         'Utilisateur'}
                      </span>
                      <span className="comment-date">{formatDate(comment.created_at)}</span>
                    </div>
                    <p>{comment.content}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <form onSubmit={handleComment} className="comment-form">
            <input
              type="text"
              className="comment-input"
              placeholder="Écrire un commentaire..."
              value={commentContent}
              onChange={(e) => setCommentContent(e.target.value)}
            />
            <button
              type="submit"
              className="comment-submit"
              disabled={!commentContent.trim() || createCommentMutation.isLoading}
            >
              Publier
            </button>
          </form>
        </div>
      )}
      
      {showCartPreview && post.shared_cart && (
        <SharedCartPreview
          cartId={post.shared_cart.id}
          cartName={post.shared_cart.name}
          onClose={() => setShowCartPreview(false)}
        />
      )}

      <RepostModal
        post={isRepost && originalPost ? originalPost : post}
        isOpen={showRepostModal}
        onClose={() => setShowRepostModal(false)}
        onSuccess={handleRepostSuccess}
      />

      {/* Message de succès/suppression avec animation - rendu via portal */}
      {showSuccessMessage && createPortal(
        <div className="success-message-overlay-post" onClick={() => setShowSuccessMessage(false)}>
          <div className="success-message-modal-post" onClick={(e) => e.stopPropagation()}>
            {isDeleteAction ? (
              refuseAnimation ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                  <Lottie
                    lottieRef={refuseLottieRef}
                    animationData={refuseAnimation}
                    loop={false}
                    autoplay={true}
                    style={{ width: 120, height: 120, margin: '0 auto' }}
                  />
                </div>
              ) : (
                <div style={{ width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '60px', color: '#ff6b6b', fontWeight: 'bold' }}>✕</div>
              )
            ) : (
              successAnimation ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                  <Lottie
                    lottieRef={successLottieRef}
                    animationData={successAnimation}
                    loop={false}
                    autoplay={true}
                    style={{ width: 120, height: 120, margin: '0 auto' }}
                  />
                </div>
              ) : (
                <div style={{ width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '60px', color: '#4a9eff', fontWeight: 'bold' }}>✓</div>
              )
            )}
            <h3>{successMessage}</h3>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default PostCard

